import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetFollowUpModal = ({ open, onOpenChange, lead, onSuccess }) => {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const quickDates = [
    { label: 'Tomorrow', days: 1 },
    { label: 'In 2 days', days: 2 },
    { label: 'In 3 days', days: 3 },
    { label: 'Next week', days: 7 },
  ];

  const handleQuickDate = (days) => {
    setDate(addDays(new Date(), days));
  };

  const handleSubmit = async () => {
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/leads/${lead.id}`, {
        next_follow_up_date: format(date, 'yyyy-MM-dd'),
        next_follow_up_time: time,
      });
      toast.success('Follow up date set');
      setDate(null);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Failed to set follow up date');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border" data-testid="follow-up-modal">
        <DialogHeader>
          <DialogTitle>Set Follow Up</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="p-3 bg-secondary/50 rounded-lg">
            <p className="font-semibold">{lead.full_name}</p>
            <p className="text-sm text-muted-foreground">{lead.company_name}</p>
          </div>

          {/* Quick date buttons */}
          <div className="space-y-2">
            <Label>Quick Select</Label>
            <div className="flex flex-wrap gap-2">
              {quickDates.map(qd => (
                <Button
                  key={qd.label}
                  variant="outline"
                  size="sm"
                  className="border-border"
                  onClick={() => handleQuickDate(qd.days)}
                >
                  {qd.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Date picker */}
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal border-border"
                  data-testid="follow-up-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time input */}
          <div className="space-y-2">
            <Label>Time</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-secondary border-border"
              data-testid="follow-up-time"
            />
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={saving || !date}
            data-testid="save-follow-up-btn"
          >
            {saving ? 'Saving...' : 'Set Follow Up'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetFollowUpModal;
